import React, { useEffect, useState, useRef, useCallback } from 'react';
import SlideShell from '../../SlideShell';
import IntroHero from '../../../../components/Onboarding/IntroHero';
import ReviewCard from '../../../../components/Onboarding/ReviewCard';

// images
import trustpilotBanner from '../../../../images/trustpilotBanner.png';
import reviewImage1 from '../../../../images/reviewImages/reviewImage1.webp';
import reviewImage2 from '../../../../images/reviewImages/reviewImage2.webp';
import reviewImage3 from '../../../../images/reviewImages/reviewImage3.webp';
import reviewImage4 from '../../../../images/reviewImages/reviewImage4.webp';
import reviewImage5 from '../../../../images/reviewImages/reviewImage5.webp';

export default function ReviewsIntroSlide({
  title,
  subtitle,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  nextLabel = 'Continue',
  animationDirection = 'forward',
  isTransitioning = false,
  theme = 'dark',

  // hero
  heroImage = trustpilotBanner,
  heroTitle = 'We can help.',
  heroDescription = 'We’ve helped 1,000+ people in their hair restoration journey so far.',

  // reviews
  reviews = [
    {
      id: 'r1',
      avatarSrc: reviewImage1,
      name: 'Raul M.',
      hashtag: 'raulml99',
      rating: 5,
      reviewTitle: 'Fantastic',
      reviewText:
        "Just used it and it's fantastic - it recommended me three clinics and honestly I had 2 already under my eye so it's definitely not random results!",
    },
    {
      id: 'r2',
      avatarSrc: reviewImage2,
      name: 'Mike R.',
      hashtag: 'themikerowl',
      rating: 5,
      reviewTitle: 'Good for beginners',
      reviewText:
        "It's a good app, it gives you contacts and information you maybe wouldn't have known about. Definitely good for someone beginning and starting out in the journey of a hair transplant.",
    },
    {
      id: 'r3',
      avatarSrc: reviewImage3,
      name: 'Dennis D.',
      hashtag: 'dennyd78',
      rating: 5,
      reviewTitle: 'Useful, great job',
      reviewText:
        'Useful website, I think it did a great job. I will use it further.',
    },
    {
      id: 'r4',
      avatarSrc: reviewImage4,
      name: 'Burak K.',
      hashtag: 'burakko_20',
      rating: 5,
      reviewTitle: 'Better than expected!',
      reviewText:
        'The whole experience was better than I expected. Thanks for everything.',
    },
    {
      id: 'r5',
      avatarSrc: reviewImage5,
      name: 'Batu K.',
      hashtag: 'bking12',
      rating: 5,
      reviewTitle: 'Best Partner Clinics!',
      reviewText:
        'They have the best clinics partners in Turkey!',
    },
  ],
}) {
  // Hero animation → stat → cards (chained by real animationend events)
  const [heroMode, setHeroMode] = useState('splash');
  const [heroDone, setHeroDone] = useState(false);
  const [statShown, setStatShown] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [ctaReady, setCtaReady] = useState(false);
  const [skipTick, setSkipTick] = useState(0); // increment to force hero skip

  const heroTimerRef = useRef(null);

  useEffect(() => {
    const delay = 2000;
    heroTimerRef.current = setTimeout(() => setHeroMode('compact'), delay);
    return () => heroTimerRef.current && clearTimeout(heroTimerRef.current);
  }, []);

  useEffect(() => {
    if (!heroDone) return;
    setStatShown(true);
  }, [heroDone]);

  const handleStatAnimEnd = () => {
    // Ensure at least the first card is allowed, but never decrease if user clicked ahead
    setVisibleCount((c) => (c < 1 ? 1 : c));
  };

  useEffect(() => {
    setCtaReady(visibleCount >= Math.min(3, reviews.length));
  }, [visibleCount, reviews.length]);

  const isDark = theme === 'dark';
  const pageBg = isDark ? 'bg-mm_black' : 'bg-white';

  // If SlideShell reserves ~64–96px for footer, this cancels it at the very end.
  const CANCEL_RESERVE_PX = 72;

  const advance = useCallback(() => {
    // Phase 1: splash -> compact (movement)
    if (heroMode === 'splash') {
      if (heroTimerRef.current) { clearTimeout(heroTimerRef.current); heroTimerRef.current = null; }
      setHeroMode('compact');
      // Force-complete movement instantly
      setSkipTick((t) => t + 1);
      return;
    }

    // If compact movement is ongoing but not marked done yet, force-complete
    if (!heroDone) {
      setSkipTick((t) => t + 1);
      return;
    }

    // Phase 2: show stat gate
    if (!statShown) {
      setStatShown(true);
      setVisibleCount((c) => (c < 1 ? 1 : c));
      return;
    }

    // Phase 3: reveal reviews one by one
    setVisibleCount((c) => (c < reviews.length ? c + 1 : c));
  }, [heroMode, heroDone, statShown, reviews.length]);

  const touchHandlers = {
    onPointerDown: advance,
    onClick: advance,
    onTouchStart: advance,
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); advance(); } },
  };

  return (
    <div className={`min-h-screen ${pageBg}`} {...touchHandlers}>
      <style>{`
        @media (min-width: 1024px) {
          .hide-scrollbar-desktop { scrollbar-width: none; -ms-overflow-style: none; }
          .hide-scrollbar-desktop::-webkit-scrollbar { display: none; width: 0; height: 0; }
        }
      `}</style>
      <SlideShell
        title={title}
        subtitle={subtitle}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={onNext}
        onBack={onBack}
        nextLabel={nextLabel}
        theme={theme}
        animationDirection={animationDirection}
        isTransitioning={isTransitioning}
        scrollable
        contentJustify="start"
        showPrimary={ctaReady}
        // 👇 If SlideShell supports it, this removes bottom content padding cleanly
        contentClassName="pb-0 hide-scrollbar-desktop"
      >
        <IntroHero
          imageSrc={heroImage}
          title={heroTitle}
          description={heroDescription}
          theme={theme}
          mode={heroMode}
          onCompactEnd={() => setHeroDone(true)}
          showCompactStat={statShown}
          durationMs={1600}
          easing="cubic-bezier(0.22, 1, 0.36, 1)"
          forceSkip={skipTick}
        />

        <style>{`
          @keyframes fadeSlideUp {
            0% { opacity: 0; transform: translateY(14px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeOpacityUp {
            0% { opacity: 0; transform: translateY(6px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .cardReveal {
            opacity: 0;
            animation: fadeOpacityUp 650ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
          }
          @media (prefers-reduced-motion: reduce) {
            .cardReveal { animation: fadeOpacityUp 1ms linear forwards !important; }
          }
        `}</style>

        {heroDone && (
          <div className="cardReveal" onAnimationEnd={handleStatAnimEnd} />
        )}

        {heroDone && (
          <div className="flex flex-col gap-4">
            {reviews.slice(0, visibleCount).map((r, idx) => (
              <div
                key={r.id}
                className="cardReveal"
                onAnimationEnd={() => {
                  if (idx + 1 === visibleCount && visibleCount < reviews.length) {
                    setVisibleCount(visibleCount + 1);
                  }
                }}
              >
                <ReviewCard
                  avatarSrc={r.avatarSrc}
                  name={r.name}
                  hashtag={r.hashtag}
                  rating={r.rating}
                  reviewTitle={r.reviewTitle}
                  reviewText={r.reviewText}
                  theme={theme}
                />
              </div>
            ))}

            {/* Three dots at the very bottom */}
            {visibleCount === reviews.length && (
              <div
                className="cardReveal flex items-center justify-center py-2"
                // 👇 Fallback: cancel SlideShell bottom reserve with a small negative margin
                style={{ marginBottom: `-${CANCEL_RESERVE_PX}px` }}
              >
                <span className={`${isDark ? 'bg-gray-500' : 'bg-gray-400'} mx-1 h-1.5 w-1.5 rounded-full`} />
                <span className={`${isDark ? 'bg-gray-500' : 'bg-gray-400'} mx-1 h-1.5 w-1.5 rounded-full`} />
                <span className={`${isDark ? 'bg-gray-500' : 'bg-gray-400'} mx-1 h-1.5 w-1.5 rounded-full`} />
              </div>
            )}
          </div>
        )}
      </SlideShell>
    </div>
  );
}
